import React, { useState, useEffect } from 'react';
import Modal from '../../../components/Modal';
import AddInternalAccountForm, { InternalAccountFormData } from './AddInternalAccountForm';
import { 
  getInternalAccountListAPI, 
  saveInternalAccountAPI, 
  deleteInternalAccountAPI,
  InternalAccount,
  InternalAccountSearchRequest 
} from '../../../services/adminApi';
import { ToastContainer } from '../../../components/Toast';
import { useToast } from '../../../hooks/useToast';
import { useModulePermission } from '../../../hooks/useModulePermission';
import styles from './InternalAccounts.module.css';

const InternalAccounts: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [accounts, setAccounts] = useState<InternalAccount[]>([]);
  const { toasts, removeToast, showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [editingAccount, setEditingAccount] = useState<InternalAccount | null>(null);
  const permissions = useModulePermission('Internal Account');

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const searchRequest: InternalAccountSearchRequest = {
        searchText: '',
        pageNo: 1,
        pageSize: 100,
      };
      const response = await getInternalAccountListAPI(searchRequest);
      if (response.isSuccess && response.data) {
        setAccounts(response.data);
      } else {
        showError(response.message || 'Failed to load internal accounts');
      }
    } catch (error) {
      console.error('Failed to load internal accounts:', error);
      showError('Failed to load internal accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAccount = () => {
    setEditingAccount(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAccount(null);
  };

  const handleSubmit = async (formData: InternalAccountFormData) => {
    try {
      const openingBalance = parseFloat(formData.openingBalance) || 0;
      // If currentBal is not set or is 0, use openingBalance as the current balance
      const currentBalance = parseFloat(formData.currentBal) || openingBalance;

      const accountPayload: InternalAccount = {
        id: editingAccount?.id,
        accountName: formData.accountName,
        accountTypeId: parseInt(formData.accountType) || 1,
        bankName: formData.bankName,
        holderName: formData.holderName,
        accountNo: formData.accountNo,
        ifscCode: formData.ifscCode,
        branchName: formData.branchName,
        openingBal: openingBalance,
        openingDate: formData.openingDate,
        currentBal : currentBalance,
        isActive: formData.status === 'Active' || formData.status === 'active',
      };

      console.log('Sending Internal Account Payload:', accountPayload);

      const response = await saveInternalAccountAPI(accountPayload);
      
      if (response.isSuccess) {
        showSuccess(editingAccount ? 'Internal account updated successfully!' : 'Internal account created successfully!');
        await loadData();
        setIsModalOpen(false);
        setEditingAccount(null);
      } else {
        showError(response.message || 'Failed to save internal account');
      }
    } catch (error) {
      console.error('Failed to save internal account:', error);
      showError('Failed to save internal account. Please try again.');
    }
  };

  const handleEdit = (account: InternalAccount) => {
    setEditingAccount(account);
    setIsModalOpen(true);
  };

  const handleDelete = async (account: InternalAccount) => {
    if (window.confirm(`Are you sure you want to delete ${account.accountName}?`)) {
      try {
        const response = await deleteInternalAccountAPI(account.id || 0);
        if (response.isSuccess) {
          await loadData();
          showSuccess('Internal account deleted successfully!');
        } else {
          showError(response.message || 'Failed to delete internal account');
        }
      } catch (error) {
        console.error('Failed to delete internal account:', error);
        showError('Failed to delete internal account. Please try again.');
      }
    }
  };

  return (
    <div className={styles.container}>
      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>Internal Accounts</h1>
          <p className={styles.subtitle}>Manage your business bank accounts, cash, and wallets</p>
        </div>
        {permissions.canAdd && (
          <button className={styles.addButton} onClick={handleAddAccount}>
            + Add Account
          </button>
        )}
      </div>

      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.loading}>Loading internal accounts...</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>NAME</th>
                <th>TYPE</th>
                <th>BANK/DETAILS</th>
                <th>OPENING BAL</th>
                <th>CURRENT BAL</th>
                <th>STATUS</th>
                {(permissions.canEdit || permissions.canVoid) && <th>ACTIONS</th>}
              </tr>
            </thead>
            <tbody>
              {accounts.length === 0 ? (
                <tr>
                  <td colSpan={permissions.canEdit || permissions.canVoid ? 7 : 6} className={styles.emptyState}>No internal accounts found</td>
                </tr>
              ) : (
                accounts.map((account) => (
                  <tr key={account.id}>
                    <td className={styles.nameCell}>{account.accountName}</td>
                  <td>
                      <span className={`${styles.typeBadge} ${styles[account.accountType?.toLowerCase().replace(' ', '') || '']}`}>
                        {account.accountType || account.accountTypeId}
                      </span>
                    </td>
                    <td>
                      {account.bankName && <div><strong>{account.bankName}</strong></div>}
                      {account.accountNo && <div>{account.accountNo}</div>}
                      {account.holderName && <div className={styles.holderName}>{account.holderName}</div>}
                    </td>
                    <td>₹{(account.openingBal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td>₹{(account.currentBal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td>
                      <span className={`${styles.statusBadge} ${styles[account.isActive ? 'active' : 'inactive']}`}>
                        {account.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {(permissions.canEdit || permissions.canVoid) && (
                      <td>
                        <div className={styles.actions}>
                          {permissions.canEdit && (
                            <button 
                              className={styles.actionIcon} 
                              onClick={() => handleEdit(account)}
                              title="Edit"
                            >
                              ✏️
                            </button>
                          )}
                          {permissions.canVoid && (
                            <button 
                              className={`${styles.actionIcon} ${styles.deleteIcon}`}
                              onClick={() => handleDelete(account)}
                              title="Delete"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <Modal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal} 
        title={editingAccount ? 'Edit Internal Account' : 'Add New Account'}
      >
        <AddInternalAccountForm 
          onSubmit={handleSubmit} 
          onCancel={handleCloseModal}
          initialData={editingAccount ? {
            accountName: editingAccount.accountName,
            accountType: editingAccount.accountTypeId?.toString() || '1',
            bankName: editingAccount.bankName || '',
            holderName: editingAccount.holderName || '',
            accountNo: editingAccount.accountNo || '',
            ifscCode: editingAccount.ifscCode || '',
            branchName: editingAccount.branchName || '',
            openingBalance: editingAccount.openingBal.toString(),
            openingDate: editingAccount.openingDate ? editingAccount.openingDate.split('T')[0] : new Date().toISOString().split('T')[0],
            status: editingAccount.isActive ? 'Active' : 'Inactive',
          } : undefined}
        />
      </Modal>
    </div>
  );
};

export default InternalAccounts;
